import type { FormEvent } from 'react';

import ErrorBanner from './ErrorBanner';
import { ExecutionGrantDurationField } from './ExecutionGrantDurationField';
import { TOOL_SCOPES } from '../constants';
import type { ConversationAppSecret, ExecGrant, OpsSecret, ToolTokenStatus } from '../types';
import { parseExecutionGrantDuration, type ExecutionGrantDurationUnit } from '../utils/executionGrantDuration';

type ConversationSecretsModalProps = {
  open: boolean;
  busy: boolean;
  error: string | null;
  opsSecrets: OpsSecret[];
  overridesByKey: Map<string, ConversationAppSecret>;
  draftKey: string;
  draftType: 'env' | 'file';
  draftValue: string;
  draftDescription: string;
  selectedOpsSecret: OpsSecret | null;
  onClose: () => void;
  onRefresh: () => void | Promise<void>;
  onDismissError: () => void;
  onDraftKeyChange: (key: string) => void;
  onDraftTypeChange: (secretType: 'env' | 'file') => void;
  onDraftValueChange: (value: string) => void;
  onDraftDescriptionChange: (value: string) => void;
  onResetOverride: (id: string) => void | Promise<void>;
  onSaveOverride: () => void | Promise<void>;
};

export function ConversationSecretsModal({
  open,
  busy,
  error,
  opsSecrets,
  overridesByKey,
  draftKey,
  draftType,
  draftValue,
  draftDescription,
  selectedOpsSecret,
  onClose,
  onRefresh,
  onDismissError,
  onDraftKeyChange,
  onDraftTypeChange,
  onDraftValueChange,
  onDraftDescriptionChange,
  onResetOverride,
  onSaveOverride
}: ConversationSecretsModalProps) {
  if (!open) {
    return null;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h3>Conversation Secrets</h3>
          <div className="row">
            <button onClick={() => void onRefresh()} disabled={busy}>
              Refresh
            </button>
            <button type="button" className="ghost" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <div className="stack">
          <p className="status">
            Runtime resolution: conversation override → app default → generated from ops spec. Reset
            removes the conversation override.
          </p>
          {error && <ErrorBanner message={error} onDismiss={onDismissError} />}
          {opsSecrets.length === 0 ? (
            <p className="status">No ops secrets found for this app.</p>
          ) : (
            <div className="stack">
              <div className="status">Overrides are scoped to this conversation and app.</div>
              <ul className="list">
                {opsSecrets.map((secret) => {
                  const override = overridesByKey.get(secret.key);
                  return (
                    <li key={secret.key}>
                      <div className="row row--between">
                        <div className="stack">
                          <div className="row">
                            <span className="mono">{secret.key}</span>
                            <span className="hint">{secret.secret_type ?? 'env'}</span>
                            {override ? (
                              <span className="badge badge--warning">override</span>
                            ) : (
                              <span className="badge">default</span>
                            )}
                          </div>
                          {secret.description && <div className="status">{secret.description}</div>}
                          {(secret.secret_type ?? 'env') === 'file' && (
                            <div className="status">
                              Path: {secret.file_path ?? '—'} ({secret.file_mode ?? '0600'})
                              {secret.file_owner ? ` owner ${secret.file_owner}` : ''}
                            </div>
                          )}
                        </div>
                        <div className="row">
                          <button
                            type="button"
                            className="ghost compact"
                            onClick={() => {
                              onDraftKeyChange(secret.key);
                              onDraftTypeChange((secret.secret_type ?? 'env') as 'env' | 'file');
                              onDraftDescriptionChange(
                                override?.description ?? secret.description ?? ''
                              );
                            }}
                            disabled={busy}
                          >
                            {override ? 'Update override' : 'Set override'}
                          </button>
                          {override && (
                            <button
                              type="button"
                              className="secondary compact"
                              onClick={() => void onResetOverride(override.id)}
                              disabled={busy}
                            >
                              Reset
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <details open>
                <summary>Set conversation override</summary>
                <div className="stack stack--spaced">
                  <label className="row">
                    <span className="field-label--sm">Key</span>
                    <select
                      className="field-control"
                      value={draftKey}
                      onChange={(event) => {
                        const key = event.target.value;
                        onDraftKeyChange(key);
                        const spec = opsSecrets.find((secret) => secret.key === key);
                        if (!spec) {
                          return;
                        }
                        const secretType = (spec.secret_type ?? 'env') as 'env' | 'file';
                        onDraftTypeChange(secretType);
                        if (spec.description) {
                          onDraftDescriptionChange(spec.description);
                        }
                      }}
                      disabled={busy}
                    >
                      {opsSecrets.map((secret) => (
                        <option key={secret.key} value={secret.key}>
                          {secret.key}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="row">
                    <span className="field-label--sm">Type</span>
                    <span className="mono">
                      {selectedOpsSecret?.secret_type ?? draftType}
                    </span>
                  </div>
                  {(selectedOpsSecret?.secret_type ?? draftType) === 'file' && (
                    <>
                      <div className="status">
                        Path: <span className="mono">{selectedOpsSecret?.file_path ?? '—'}</span> |
                        Mode: <span className="mono">{selectedOpsSecret?.file_mode ?? '0600'}</span>
                        {selectedOpsSecret?.file_owner ? (
                          <>
                            {' '}
                            | Owner: <span className="mono">{selectedOpsSecret.file_owner}</span>
                          </>
                        ) : null}
                      </div>
                      <label className="row">
                        <span className="field-label--sm">Upload file</span>
                        <input
                          className="field-control"
                          type="file"
                          onChange={async (event) => {
                            const file = event.target.files?.[0];
                            if (!file) {
                              return;
                            }
                            const content = await file.text();
                            onDraftValueChange(content);
                            if (!draftDescription.trim()) {
                              onDraftDescriptionChange(`Imported from ${file.name}`);
                            }
                          }}
                          disabled={busy}
                        />
                      </label>
                    </>
                  )}
                  <label className="stack">
                    <span>Value</span>
                    <textarea
                      rows={4}
                      value={draftValue}
                      onChange={(event) => onDraftValueChange(event.target.value)}
                      placeholder="Enter override value"
                      disabled={busy}
                    />
                  </label>
                  <label className="row">
                    <span className="field-label--sm">Description</span>
                    <input
                      className="field-control"
                      type="text"
                      value={draftDescription}
                      onChange={(event) => onDraftDescriptionChange(event.target.value)}
                      placeholder="Optional note"
                      disabled={busy}
                    />
                  </label>
                  <div className="row">
                    <button
                      type="button"
                      onClick={() => void onSaveOverride()}
                      disabled={busy || !draftKey.trim() || !draftValue.trim()}
                    >
                      {busy ? 'Saving…' : 'Save override'}
                    </button>
                    <span className="status">Reset by deleting the override above.</span>
                  </div>
                </div>
              </details>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type ConversationToolAccessModalProps = {
  open: boolean;
  busy: boolean;
  status: ToolTokenStatus | null;
  tokenValue: string | null;
  scopes: string[];
  execGrants: ExecGrant[];
  execGrantKey: string | null;
  execGrantName: string;
  execGrantDurationAmount: string;
  execGrantDurationUnit: ExecutionGrantDurationUnit;
  execGrantPrivileged: boolean;
  execGrantExecutorRefs: string;
  execGrantMaxUses: string;
  onClose: () => void;
  onRefresh: () => void | Promise<void>;
  onCopyToken: () => void | Promise<void>;
  onToggleScope: (scope: string) => void;
  onCreateToken: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
  onRevokeToken: () => void | Promise<void>;
  onExecGrantNameChange: (value: string) => void;
  onExecGrantDurationAmountChange: (value: string) => void;
  onExecGrantDurationUnitChange: (value: ExecutionGrantDurationUnit) => void;
  onExecGrantPrivilegedChange: (value: boolean) => void;
  onExecGrantExecutorRefsChange: (value: string) => void;
  onExecGrantMaxUsesChange: (value: string) => void;
  onRefreshExecGrants: () => void | Promise<void>;
  onCreateExecGrant: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
  onRevokeExecGrant: (grantId: string) => void | Promise<void>;
  onCopyExecGrantKey: () => void | Promise<void>;
};

export function ConversationToolAccessModal({
  open,
  busy,
  status,
  tokenValue,
  scopes,
  execGrants,
  execGrantKey,
  execGrantName,
  execGrantDurationAmount,
  execGrantDurationUnit,
  execGrantPrivileged,
  execGrantExecutorRefs,
  execGrantMaxUses,
  onClose,
  onRefresh,
  onCopyToken,
  onToggleScope,
  onCreateToken,
  onRevokeToken,
  onExecGrantNameChange,
  onExecGrantDurationAmountChange,
  onExecGrantDurationUnitChange,
  onExecGrantPrivilegedChange,
  onExecGrantExecutorRefsChange,
  onExecGrantMaxUsesChange,
  onRefreshExecGrants,
  onCreateExecGrant,
  onRevokeExecGrant,
  onCopyExecGrantKey
}: ConversationToolAccessModalProps) {
  if (!open) {
    return null;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h3>Tool access (MCP)</h3>
          <div className="row">
            <button onClick={() => void onRefresh()}>Refresh</button>
            <button type="button" className="ghost" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <div className="stack">
          {status?.message && <div className="status">{status.message}</div>}
          <div className="row">
            <span className="mono">Status: {status?.status ?? 'unknown'}</span>
            {status?.expires_at && (
              <span className="mono">
                Expires {new Date(status.expires_at).toLocaleString()}
              </span>
            )}
          </div>
          <div className="status">
            Scopes: {status?.scopes?.length ? status.scopes.join(', ') : '—'}
          </div>
          {tokenValue && (
            <div className="stack">
              <div className="row">
                <span className="mono">Token (shown once)</span>
                <button type="button" className="ghost" onClick={() => void onCopyToken()}>
                  Copy
                </button>
              </div>
              <textarea className="log-box" rows={3} readOnly value={tokenValue} />
            </div>
          )}
          <form onSubmit={(event) => void onCreateToken(event)} className="stack">
            <div className="stack">
              <span className="status">Scopes</span>
              {TOOL_SCOPES.map((scope) => (
                <label key={scope.value} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope.value)}
                    onChange={() => onToggleScope(scope.value)}
                    disabled={status?.status === 'disabled'}
                  />
                  <span>
                    {scope.label} <span className="status">({scope.value})</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="row">
              <button type="submit" disabled={busy || status?.status === 'disabled'}>
                {busy ? 'Working...' : 'Rotate now'}
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => void onRevokeToken()}
                disabled={!status?.active || busy}
              >
                Revoke
              </button>
            </div>
            <p className="status">Tokens rotate automatically on each new message.</p>
            <p className="status">
              Active tokens are injected into agent containers as
              <span className="mono"> PROJ_CREATOR_TOOL_TOKEN</span>.
            </p>
          </form>
          <div className="stack">
            <div className="row row--between">
              <h4>Execution grants</h4>
              <button type="button" className="ghost" onClick={() => void onRefreshExecGrants()}>
                Refresh grants
              </button>
            </div>
            {execGrantKey && (
              <div className="stack">
                <div className="row">
                  <span className="mono">Grant key (shown once)</span>
                  <button type="button" className="ghost" onClick={() => void onCopyExecGrantKey()}>
                    Copy
                  </button>
                </div>
                <textarea className="log-box" rows={3} readOnly value={execGrantKey} />
              </div>
            )}
            <form onSubmit={(event) => void onCreateExecGrant(event)} className="stack">
              <label>
                Name
                <input
                  value={execGrantName}
                  onChange={(event) => onExecGrantNameChange(event.target.value)}
                  disabled={busy}
                />
              </label>
              <div className="form-grid">
                <label>
                  Max commands
                  <input
                    type="number"
                    min={1}
                    value={execGrantMaxUses}
                    onChange={(event) => onExecGrantMaxUsesChange(event.target.value)}
                    placeholder="Unlimited"
                    disabled={busy}
                  />
                </label>
              </div>
              <ExecutionGrantDurationField
                amount={execGrantDurationAmount}
                unit={execGrantDurationUnit}
                onAmountChange={onExecGrantDurationAmountChange}
                onUnitChange={onExecGrantDurationUnitChange}
                disabled={busy}
              />
              <label>
                Executor ids
                <input
                  value={execGrantExecutorRefs}
                  onChange={(event) => onExecGrantExecutorRefsChange(event.target.value)}
                  placeholder="All visible executors"
                  disabled={busy}
                />
              </label>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={execGrantPrivileged}
                  onChange={(event) => onExecGrantPrivilegedChange(event.target.checked)}
                  disabled={busy}
                />
                <span>Allow privileged mode</span>
              </label>
              <div className="row">
                <button type="submit" disabled={busy ||
                  parseExecutionGrantDuration(execGrantDurationAmount, execGrantDurationUnit).minutes === null}>
                  Create grant
                </button>
              </div>
            </form>
            <div className="stack">
              {execGrants.length === 0 && <div className="status">No execution grants.</div>}
              {execGrants.map((grant) => (
                <div key={grant.id} className="approval-card">
                  <div className="row row--between">
                    <div>
                      <div className="approval-card__title">{grant.name}</div>
                      <div className="approval-card__detail">
                        {grant.active ? 'Active' : 'Inactive'} · {grant.modes.join(', ')} ·{' '}
                        {grant.executor_scope === 'specific'
                          ? grant.executor_refs.map((ref) => `executor:${ref}`).join(', ')
                          : 'all visible executors'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="secondary"
                      disabled={!grant.active || busy}
                      onClick={() => void onRevokeExecGrant(grant.id)}
                    >
                      Revoke
                    </button>
                  </div>
                  <div className="status">
                    Used {grant.use_count}
                    {grant.max_uses ? `/${grant.max_uses}` : ''} · Expires{' '}
                    {new Date(grant.expires_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
